import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { IftaLabelModule } from 'primeng/iftalabel';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { CrmClient } from 'src/app/crm/crm.model';
import { CrmService } from 'src/app/crm/crm.service';
import { PageDataPanelComponent } from 'src/app/shared/page-layout/page-data-panel.component';
import { WarehousingMasterData } from 'src/app/warehousing/warehousing.model';
import { WarehousingService } from 'src/app/warehousing/warehousing.service';
import { PosmProject, PosmStockAvailability } from '../../models/posm-project.model';
import {
    PosmProjectItem,
    PosmProjectItemSource,
    PosmProjectItemSourceAllocationTarget,
    PosmProjectItemSourceReservation
} from '../../models/posm-project-item.model';
import { PosmService } from '../../posm.service';

type WarehousingWarehouseOption = {
    label: string;
    value: number;
    locationId?: number;
    supplierClientId?: number;
    supplierClientName?: string;
    warehouse: WarehousingMasterData;
};

type SourceAllocationEntry = {
    reservation: PosmProjectItemSourceReservation;
    target: PosmProjectItemSourceAllocationTarget;
};

@Component({
    selector: 'app-posm-project-materials',
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        DatePickerModule,
        InputNumberModule,
        InputTextModule,
        InputGroupModule,
        InputGroupAddonModule,
        IftaLabelModule,
        SelectModule,
        TableModule,
        TagModule,
        TextareaModule,
        ToastModule,
        PageDataPanelComponent
    ],
    providers: [MessageService],
    templateUrl: './posm-project-materials.component.html',
    styleUrls: ['./posm-project-materials.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PosmProjectMaterialsComponent {
    private posmService = inject(PosmService);
    private crmService = inject(CrmService);
    private warehousingService = inject(WarehousingService);
    private messageService = inject(MessageService);

    public project = input.required<PosmProject>();
    public onDataChanged = output<void>();

    public projectItems = signal<PosmProjectItem[]>([]);
    public itemSources = signal<Record<number, PosmProjectItemSource[]>>({});
    public sourceDrafts = signal<Record<number, PosmProjectItemSource>>({});
    public warehouseOptions = signal<WarehousingWarehouseOption[]>([]);
    public supplierOptions = signal<any[]>([]);
    public conditionOptions = signal<WarehousingMasterData[]>([]);
    public stockBySource = signal<Record<number, PosmStockAvailability>>({});
    public loading = signal(false);
    public sourceSavingIds = signal<Record<string, boolean>>({});

    public sourceTypeOptions = [
        { label: 'Company Warehouse', value: 'COMPANY_WAREHOUSE' },
        { label: '3rd Party Supplier', value: 'THIRD_PARTY_SUPPLIER' }
    ];

    public totalRequired = computed(() =>
        this.projectItems().reduce((total, item) => total + (Number(item.quantity) || 0), 0)
    );
    public totalAllocated = computed(() =>
        this.projectItems().reduce((total, item) => total + this.allocatedQuantity(item), 0)
    );

    private loadedProjectId?: number;

    constructor() {
        effect(() => {
            const project = this.project();
            if (project?.id && project.id !== this.loadedProjectId) {
                this.loadedProjectId = project.id;
                this.loadProjectData(project);
            }
        });
    }

    loadProjectData(project: PosmProject) {
        const companyId = this.projectCompanyId();
        if (!project.id || !companyId) return;
        this.loading.set(true);
        forkJoin({
            items: this.posmService.getProjectItems(project.id, companyId),
            warehouses: this.warehousingService.getMasterData('WAREHOUSE', { activeStatus: 'ACTIVE' }).pipe(
                catchError(() => of({ data: [] as WarehousingMasterData[] } as any))
            ),
            suppliers: this.crmService.getClients(companyId, ['WAREHOUSE_SUPPLIER', 'SUPPLIER']).pipe(
                catchError(() => of([] as CrmClient[]))
            ),
            conditions: this.warehousingService.getMasterData('ITEM_CONDITION', { activeStatus: 'ACTIVE' }).pipe(
                catchError(() => of({ data: [] as WarehousingMasterData[] } as any))
            )
        }).subscribe({
            next: ({ items, warehouses, suppliers, conditions }) => {
                this.projectItems.set(items || []);
                this.warehouseOptions.set(this.buildWarehouseOptions(warehouses?.data || []));
                this.conditionOptions.set((conditions?.data || []).filter((condition: WarehousingMasterData) => condition.isActive !== false));
                this.supplierOptions.set(this.buildSupplierOptions(suppliers || []));
                this.loadSources(project.id!, companyId, items || []);
            },
            error: () => {
                this.loading.set(false);
                this.messageService.add({ severity: 'error', summary: 'Stands & Materials', detail: 'Unable to load material requirements.' });
            }
        });
    }

    loadSources(projectId: number, companyId: number, items: PosmProjectItem[]) {
        const sourceCalls = items
            .filter(item => !!item.id)
            .map(item => this.posmService.getProjectItemSources(projectId, item.id!, companyId).pipe(
                catchError(() => of([] as PosmProjectItemSource[]))
            ));
        if (!sourceCalls.length) {
            this.itemSources.set({});
            this.loading.set(false);
            return;
        }
        forkJoin(sourceCalls).subscribe({
            next: (sourceGroups) => {
                const nextSources: Record<number, PosmProjectItemSource[]> = {};
                items.filter(item => !!item.id).forEach((item, index) => {
                    nextSources[item.id!] = (sourceGroups[index] || []).map(source => this.normalizeSource(source));
                });
                this.itemSources.set(nextSources);
                this.loading.set(false);
                this.refreshAllSourceStock();
            },
            error: () => {
                this.loading.set(false);
                this.messageService.add({ severity: 'warn', summary: 'Sources', detail: 'Some source lines could not be loaded.' });
            }
        });
    }

    saveItem(item: PosmProjectItem) {
        const project = this.project();
        const companyId = this.projectCompanyId();
        if (!project.id || !item.id || !companyId) return;
        if (item.quantityEditable === false) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Quantity Locked',
                detail: item.quantityLockedReason || 'Quantity can no longer be changed for this material.'
            });
            return;
        }
        this.posmService.updateProjectItem(project.id, item.id, companyId, item).subscribe({
            next: (saved) => {
                this.projectItems.set(this.projectItems().map(row => row.id === saved.id ? saved : row));
                this.messageService.add({ severity: 'success', summary: 'Item Saved', detail: 'Material requirement was updated.' });
                this.onDataChanged.emit();
            },
            error: (error) => this.messageService.add({
                severity: 'error',
                summary: 'Item Save Failed',
                detail: error?.error?.message || error?.error || 'Unable to update material requirement.'
            })
        });
    }

    addSourceDraft(item: PosmProjectItem) {
        if (!item.id) return;
        if (!this.canAddSourceLine(item)) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Source Lines',
                detail: 'Required quantity is already fully reserved for this material.'
            });
            return;
        }
        const remaining = this.remainingQuantity(item);
        this.sourceDrafts.update(current => ({
            ...current,
            [item.id!]: {
                projectItemId: item.id,
                sourceType: 'COMPANY_WAREHOUSE',
                quantity: remaining,
                reservationStatus: 'PLANNED',
                reservations: []
            }
        }));
    }

    cancelSourceDraft(item: PosmProjectItem) {
        if (!item.id) return;
        this.sourceDrafts.update(current => {
            const copy = { ...current };
            delete copy[item.id!];
            return copy;
        });
    }

    saveSource(item: PosmProjectItem, source: PosmProjectItemSource, isDraft = false) {
        const project = this.project();
        const companyId = this.projectCompanyId();
        if (!project.id || !item.id || !companyId) return;
        const key = isDraft ? `draft-${item.id}` : `${source.id}`;
        this.sourceSavingIds.update(current => ({ ...current, [key]: true }));
        const request = source.id
            ? this.posmService.updateProjectItemSource(project.id, item.id, source.id, companyId, source)
            : this.posmService.createProjectItemSource(project.id, item.id, companyId, source);
        request.subscribe({
            next: (saved) => {
                this.sourceSavingIds.update(current => ({ ...current, [key]: false }));
                this.upsertSource(item.id!, saved);
                if (isDraft) {
                    this.sourceDrafts.update(current => {
                        const copy = { ...current };
                        delete copy[item.id!];
                        return copy;
                    });
                }
                this.refreshSourceStock(item, this.normalizeSource(saved), false);
                this.messageService.add({ severity: 'success', summary: 'Source Saved', detail: 'Material source request was saved.' });
            },
            error: (error) => {
                this.sourceSavingIds.update(current => ({ ...current, [key]: false }));
                this.messageService.add({
                    severity: 'error',
                    summary: 'Source Save Failed',
                    detail: this.sourceSaveErrorDetail(error)
                });
            }
        });
    }

    deleteSource(item: PosmProjectItem, source: PosmProjectItemSource) {
        const project = this.project();
        const companyId = this.projectCompanyId();
        if (!project.id || !item.id || !source.id || !companyId) return;
        this.posmService.deleteProjectItemSource(project.id, item.id, source.id, companyId).subscribe({
            next: () => {
                this.itemSources.update(current => ({
                    ...current,
                    [item.id!]: (current[item.id!] || []).filter(row => row.id !== source.id)
                }));
                this.messageService.add({ severity: 'success', summary: 'Source Removed', detail: 'Source line was removed.' });
            },
            error: (error) => this.messageService.add({
                severity: 'error',
                summary: 'Source Remove Failed',
                detail: error?.error?.message || error?.error || 'Unable to remove source line.'
            })
        });
    }

    refreshSourceStock(item: PosmProjectItem, source: PosmProjectItemSource, showMessage = true) {
        const itemId = item.inventoryItem?.id;
        const warehouseId = source.sourceWarehousingWarehouseId;
        if (!itemId || !warehouseId || !this.projectCompanyId() || !this.isWarehouseManagedSource(source)) return;
        this.posmService.getStockAvailability(
            this.projectCompanyId(),
            itemId,
            warehouseId,
            source.requestedConditionId ?? null
        ).subscribe({
            next: (stock) => {
                if (source.id) {
                    this.stockBySource.update(current => ({ ...current, [source.id!]: stock }));
                }
                if (showMessage) {
                    this.messageService.add({ severity: 'info', summary: 'Stock Checked', detail: 'Availability refreshed from Warehousing.' });
                }
            }
        });
    }

    reloadItemSources(item: PosmProjectItem) {
        const project = this.project();
        const companyId = this.projectCompanyId();
        if (!project.id || !item.id || !companyId) return;
        this.posmService.getProjectItemSources(project.id, item.id, companyId).subscribe(sources => {
            this.itemSources.update(current => ({
                ...current,
                [item.id!]: (sources || []).map(source => this.normalizeSource(source))
            }));
        });
    }

    refreshAllSourceStock() {
        this.projectItems().forEach(item => {
            this.sourcesForItem(item).forEach(source => this.refreshSourceStock(item, source, false));
        });
    }

    showWarehousingReservationsShortcut(): boolean {
        return this.projectItems().some(item =>
            this.sourcesForItem(item).some(source => this.isWarehouseManagedSource(source))
        );
    }

    sourcesForItem(item: PosmProjectItem): PosmProjectItemSource[] {
        return item.id ? (this.itemSources()[item.id] || []) : [];
    }

    draftForItem(item: PosmProjectItem): PosmProjectItemSource | null {
        return item.id ? (this.sourceDrafts()[item.id] || null) : null;
    }

    allocatedQuantity(item: PosmProjectItem): number {
        return this.sourcesForItem(item).reduce((total, row) => total + (Number(row.quantity) || 0), 0);
    }

    reservedQuantity(item: PosmProjectItem): number {
        return this.sourcesForItem(item).reduce((total, row) => total + (Number(row.reservedQuantity) || 0), 0);
    }

    allocatedToSchedulesQuantity(item: PosmProjectItem): number {
        return this.sourcesForItem(item).reduce((total, row) => total + this.sourceAllocatedQuantity(row), 0);
    }

    remainingQuantity(item: PosmProjectItem): number {
        return Math.max((Number(item.quantity) || 0) - this.allocatedToSchedulesQuantity(item), 0);
    }

    canAddSourceLine(item: PosmProjectItem): boolean {
        return (Number(item.quantity) || 0) > this.reservedQuantity(item);
    }

    sourceRemainingQuantity(source: PosmProjectItemSource): number {
        return Math.max((Number(source.quantity) || 0) - (Number(source.reservedQuantity) || 0), 0);
    }

    sourceReservedFreeQuantity(source: PosmProjectItemSource): number {
        return Math.max((Number(source.reservedQuantity) || 0) - this.sourceAllocatedQuantity(source), 0);
    }

    sourceAllocatedQuantity(source: PosmProjectItemSource): number {
        const seen = new Set<string>();
        return (source.reservations || []).reduce((total, reservation) => (
            total + this.reservationAllocationTargets(reservation).reduce((allocationTotal, target) => {
                const key = this.allocationTargetKey(target);
                if (seen.has(key)) {
                    return allocationTotal;
                }
                seen.add(key);
                return allocationTotal + (Number(target.allocatedQuantity) || 0);
            }, 0)
        ), 0);
    }

    sourceTypeLabel(source: PosmProjectItemSource): string {
        const value = `${source.sourceType || ''}`.toUpperCase();
        return this.sourceTypeOptions.find(option => option.value === value)?.label
            || value.replaceAll('_', ' ')
            || 'Source';
    }

    supplierLabel(source: PosmProjectItemSource): string {
        return this.supplierOptionsForSource(source).find(option => option.value === source.supplierClientId)?.label
            || source.supplierClientName
            || 'Supplier not selected';
    }

    supplierOptionsForSource(source: PosmProjectItemSource): any[] {
        const preferredSupplierId = this.preferredSupplierClientId(source);
        const options = [...this.supplierOptions()];
        if (!preferredSupplierId) {
            return options;
        }
        return options.sort((left, right) => {
            if (left.value === preferredSupplierId) return -1;
            if (right.value === preferredSupplierId) return 1;
            return `${left.label || ''}`.localeCompare(`${right.label || ''}`);
        });
    }

    sourceHeaderTitle(source: PosmProjectItemSource): string {
        return this.supplierRequired(source)
            ? this.supplierLabel(source)
            : this.sourceWarehouseLabel(source);
    }

    sourceHeaderMeta(source: PosmProjectItemSource): string {
        const segments = [
            this.isLogicalCoverageSource(source) ? 'Logical external coverage' : 'Warehouse-managed request',
            `Qty ${Number(source.quantity) || 0}`,
            this.requestedConditionLabel(source),
            source.reservationRequiredBy ? `Need by ${new Date(source.reservationRequiredBy).toLocaleDateString('en-GB')}` : null
        ].filter(Boolean);
        return segments.join(' | ');
    }

    stockText(item: PosmProjectItem, source: PosmProjectItemSource): string {
        if (this.isLogicalCoverageSource(source)) return 'Logical third-party coverage';
        const stock = source.id ? this.stockBySource()[source.id] : null;
        if (!source.sourceWarehousingWarehouseId) return 'Select Warehousing warehouse';
        if (!stock) return 'Not checked';
        return `${stock.availableQuantity || 0} available / ${stock.reservedQuantity || 0} reserved`;
    }

    stockSeverity(item: PosmProjectItem, source: PosmProjectItemSource): string {
        if (this.isLogicalCoverageSource(source)) return 'secondary';
        const stock = source.id ? this.stockBySource()[source.id] : null;
        if (!stock) return 'secondary';
        return (stock.availableQuantity || 0) >= this.sourceRemainingQuantity(source) ? 'success' : 'danger';
    }

    sourceStockUnavailableReason(item: PosmProjectItem, source: PosmProjectItemSource): string | null {
        const stock = source.id ? this.stockBySource()[source.id] : null;
        if (!stock || this.isLogicalCoverageSource(source)) return null;
        const available = stock.availableQuantity || 0;
        const required = this.sourceRemainingQuantity(source);
        if (available >= required) return null;
        const itemName = item.inventoryItem?.itemName || item.missingItemReference || 'Selected item';
        const warehouse = this.sourceWarehouseLabel(source);
        return `No usable stock for ${itemName} in ${warehouse}. ${available} available, ${required} required.`;
    }

    sourceSaving(source: PosmProjectItemSource, item?: PosmProjectItem): boolean {
        const key = source.id ? `${source.id}` : item?.id ? `draft-${item.id}` : '';
        return !!(key && this.sourceSavingIds()[key]);
    }

    sourceLocked(source: PosmProjectItemSource): boolean {
        if (this.isLogicalCoverageSource(source)) {
            return this.sourceAllocatedQuantity(source) > 0;
        }
        return (Number(source.reservedQuantity) || 0) > 0 || this.hasReservationDetail(source);
    }

    sourceLockedReason(source: PosmProjectItemSource): string | null {
        if (!this.sourceLocked(source)) {
            return null;
        }
        if (this.isLogicalCoverageSource(source)) {
            return 'Logical third-party coverage is already allocated to delivery schedules. Release schedule coverage first to change source, warehouse, condition, quantity, or due date.';
        }
        return 'Warehouse reservation already exists for this source line. Release reservations first to change source, warehouse, condition, quantity, or due date.';
    }

    reservationSeverity(status?: string): string {
        const value = `${status || ''}`.toUpperCase();
        if (value === 'RESERVED') return 'success';
        if (value === 'PARTIAL') return 'warning';
        return 'secondary';
    }

    materialStatusLabel(item: PosmProjectItem): string {
        const value = `${item.materialStatus || 'PLANNED'}`.toUpperCase();
        return {
            PLANNED: 'Planned',
            PARTIALLY_RESERVED: 'Partially Reserved',
            RESERVED: 'Reserved',
            PARTIALLY_ALLOCATED: 'Partially Allocated',
            ALLOCATED: 'Allocated',
            EXECUTION_COMMITTED: 'Execution Committed'
        }[value] || value.replaceAll('_', ' ');
    }

    materialStatusSeverity(item: PosmProjectItem): string {
        switch (`${item.materialStatus || ''}`.toUpperCase()) {
            case 'ALLOCATED':
                return 'success';
            case 'PARTIALLY_ALLOCATED':
            case 'PARTIALLY_RESERVED':
                return 'warning';
            case 'RESERVED':
            case 'EXECUTION_COMMITTED':
                return 'info';
            default:
                return 'secondary';
        }
    }

    supplierRequired(source: PosmProjectItemSource): boolean {
        return `${source.sourceType || ''}`.toUpperCase() === 'THIRD_PARTY_SUPPLIER';
    }

    coverageMode(source: PosmProjectItemSource): 'PHYSICAL' | 'LOGICAL' {
        const value = `${source.coverageMode || ''}`.toUpperCase();
        if (value === 'LOGICAL') {
            return 'LOGICAL';
        }
        if (this.supplierRequired(source)) {
            return 'LOGICAL';
        }
        return this.selectedWarehouse(source)?.warehouse?.warehouseOwnershipType === 'THIRD_PARTY_VIRTUAL'
            ? 'LOGICAL'
            : 'PHYSICAL';
    }

    isLogicalCoverageSource(source: PosmProjectItemSource): boolean {
        return this.coverageMode(source) === 'LOGICAL';
    }

    coverageModeLabel(source: PosmProjectItemSource): string {
        return this.isLogicalCoverageSource(source) ? 'Logical Coverage' : 'Physical Reservation';
    }

    coverageModeSeverity(source: PosmProjectItemSource): string {
        return this.isLogicalCoverageSource(source) ? 'secondary' : 'info';
    }

    isWarehouseManagedSource(source: PosmProjectItemSource): boolean {
        return !this.isLogicalCoverageSource(source) && !!source.sourceWarehousingWarehouseId;
    }

    onSourceWarehouseChanged(source: PosmProjectItemSource): void {
        const warehouse = this.selectedWarehouse(source);
        source.sourceWarehousingWarehouseName = warehouse?.label;
        if (!this.supplierRequired(source) && warehouse?.warehouse?.warehouseOwnershipType !== 'THIRD_PARTY_VIRTUAL') {
            source.supplierClientId = undefined;
            source.supplierClientName = undefined;
            return;
        }
        if (warehouse?.supplierClientId) {
            source.supplierClientId = warehouse.supplierClientId;
            source.supplierClientName = warehouse.supplierClientName;
        }
    }

    hasReservationDetail(source: PosmProjectItemSource): boolean {
        return !!source.reservations?.length;
    }

    legacyReservedWithoutDetail(source: PosmProjectItemSource): boolean {
        return !this.isLogicalCoverageSource(source)
            && !this.hasReservationDetail(source)
            && (Number(source.reservedQuantity) || 0) > 0;
    }

    sourceReservationGuidance(source: PosmProjectItemSource): string {
        if (this.isLogicalCoverageSource(source)) {
            return 'Third-party virtual and external supplier sources are logical coverage only. They do not create warehousing stock-unit reservations.';
        }
        if (this.supplierRequired(source)) {
            return 'Third-party supplier source lines are fulfilled outside warehouse stock reservations.';
        }
        if (!source.sourceWarehousingWarehouseId) {
            return 'Select a Warehousing warehouse before the warehouse team can allocate stock.';
        }
        if ((Number(source.reservedQuantity) || 0) >= (Number(source.quantity) || 0)) {
            return 'Warehouse reservation is complete for this request.';
        }
        return 'Warehouse team allocates exact stock units in Warehousing > Reservations.';
    }

    reservationUnitLabel(source: PosmProjectItemSource, reservation: PosmProjectItemSourceReservation): string {
        return reservation?.stockUnitCode || `Stock Unit #${reservation?.stockUnitId || '-'}`;
    }

    reservationAllocationTargets(reservation: PosmProjectItemSourceReservation): PosmProjectItemSourceAllocationTarget[] {
        return reservation?.allocationTargets || [];
    }

    sourceAllocationEntries(source: PosmProjectItemSource): SourceAllocationEntry[] {
        const seen = new Set<string>();
        const reservationEntries = (source.reservations || []).flatMap(reservation =>
            this.reservationAllocationTargets(reservation)
                .filter(target => {
                    const key = `${reservation.stockUnitId || reservation.id || 'reservation'}:${this.allocationTargetKey(target)}`;
                    if (seen.has(key)) {
                        return false;
                    }
                    seen.add(key);
                    return true;
                })
                .map(target => ({ reservation, target }))
        );
        if (reservationEntries.length || !this.isLogicalCoverageSource(source)) {
            return reservationEntries;
        }
        return (source.allocationTargets || [])
            .filter(target => {
                const key = `logical:${this.allocationTargetKey(target)}`;
                if (seen.has(key)) {
                    return false;
                }
                seen.add(key);
                return true;
            })
            .map(target => ({
                reservation: {
                    stockUnitCode: 'External coverage',
                    warehouseName: source.sourceWarehousingWarehouseName || source.sourceWarehouseName || source.supplierClientName,
                    conditionName: this.requestedConditionLabel(source),
                    quantity: target.allocatedQuantity
                },
                target
            }));
    }

    allocationTargetKey(target: PosmProjectItemSourceAllocationTarget): string {
        return `${target.deliveryScheduleId || 'schedule'}:${target.installationTaskId || 'task'}:${target.scheduleNumber || 'number'}`;
    }

    reservationAllocationLabel(target: PosmProjectItemSourceAllocationTarget): string {
        const segments = [
            target?.scheduleNumber || 'Unnumbered schedule',
            target?.installationTaskId ? `Task #${target.installationTaskId}` : null,
            target?.storeName || null
        ].filter(Boolean);
        return segments.join(' | ');
    }

    reservationAllocationMeta(target: PosmProjectItemSourceAllocationTarget): string {
        const segments = [
            target?.allocatedQuantity != null ? `Qty ${target.allocatedQuantity}` : null,
            target?.allocationStatus ? `${target.allocationStatus}`.replaceAll('_', ' ') : null,
            target?.installationDate ? `Install ${new Date(target.installationDate).toLocaleDateString('en-GB')}` : null,
            target?.jobName || null
        ].filter(Boolean);
        return segments.join(' • ');
    }

    reservationAllocatedQuantity(reservation: PosmProjectItemSourceReservation): number {
        const seen = new Set<string>();
        return this.reservationAllocationTargets(reservation).reduce((total, target) => {
            const key = this.allocationTargetKey(target);
            if (seen.has(key)) {
                return total;
            }
            seen.add(key);
            return total + (Number(target.allocatedQuantity) || 0);
        }, 0);
    }

    reservationUnallocatedQuantity(reservation: PosmProjectItemSourceReservation): number {
        return Math.max((Number(reservation.quantity) || 0) - this.reservationAllocatedQuantity(reservation), 0);
    }

    reservationAllocationStateLabel(reservation: PosmProjectItemSourceReservation): string {
        const allocated = this.reservationAllocatedQuantity(reservation);
        const reserved = Number(reservation.quantity) || 0;
        if (allocated <= 0) {
            return 'Reserved';
        }
        if (allocated >= reserved) {
            return 'Allocated';
        }
        return 'Partially Allocated';
    }

    reservationAllocationStateSeverity(reservation: PosmProjectItemSourceReservation): string {
        const label = this.reservationAllocationStateLabel(reservation);
        if (label === 'Allocated') return 'success';
        if (label === 'Partially Allocated') return 'warning';
        return 'info';
    }

    reservationLocationLabel(source: PosmProjectItemSource, reservation: PosmProjectItemSourceReservation): string {
        const segments = [
            reservation.warehouseName || source.sourceWarehousingWarehouseName || source.sourceWarehouseName || '-',
            reservation.zoneName || null,
            reservation.conditionName || this.requestedConditionLabel(source)
        ].filter(Boolean);
        return segments.join(' | ');
    }

    itemMetadataSummary(item: PosmProjectItem): string {
        const inventoryItem = item.inventoryItem;
        if (!inventoryItem) {
            return item.missingItemReference || 'Not mapped to a warehousing item yet';
        }
        const segments = [
            inventoryItem.tenantUomName || null,
            this.itemSizeLabel(item),
            inventoryItem.weightKg != null ? `${inventoryItem.weightKg} kg` : null,
            inventoryItem.brandName || null,
            inventoryItem.subBrandName || null,
            inventoryItem.hierarchyPath || null
        ].filter(Boolean);
        return segments.join(' | ');
    }

    itemSizeLabel(item: PosmProjectItem): string | null {
        const inventoryItem = item.inventoryItem;
        if (!inventoryItem) {
            return null;
        }
        const length = inventoryItem.lengthMeters;
        const breadth = inventoryItem.breadthMeters;
        const height = inventoryItem.heightMeters;
        if (length == null && breadth == null && height == null) {
            return null;
        }
        const unit = inventoryItem.lengthUnitName || 'm';
        return `${this.formatDimension(length)} x ${this.formatDimension(breadth)} x ${this.formatDimension(height)} ${unit}`;
    }

    requestedConditionLabel(source: PosmProjectItemSource): string {
        if (!source.requestedConditionId) return 'Any condition';
        return source.requestedConditionName
            || this.conditionOptions().find(condition => condition.id === source.requestedConditionId)?.name
            || `Condition #${source.requestedConditionId}`;
    }

    projectCompanyId(): number {
        return this.project().owningCompanyId || 0;
    }

    private formatDimension(value?: number | string | null): string {
        if (value == null || value === '') {
            return '-';
        }
        const numeric = Number(value);
        return Number.isFinite(numeric) ? `${numeric}` : `${value}`;
    }

    private upsertSource(itemId: number, source: PosmProjectItemSource) {
        const normalizedSource = this.normalizeSource(source);
        this.itemSources.update(current => {
            const rows = current[itemId] || [];
            const exists = rows.some(row => row.id === normalizedSource.id);
            return {
                ...current,
                [itemId]: exists
                    ? rows.map(row => row.id === normalizedSource.id ? normalizedSource : row)
                    : [...rows, normalizedSource]
            };
        });
    }

    private buildWarehouseOptions(masterWarehouses: WarehousingMasterData[]) {
        const byValue = new Map<number, WarehousingWarehouseOption>();
        (masterWarehouses || [])
            .filter(warehouse => warehouse.isActive !== false)
            .forEach(warehouse => {
                const value = warehouse.id;
                if (!value) return;
                const primaryLabel = warehouse.name
                    || warehouse.locationName
                    || warehouse.code
                    || `Warehouse #${value}`;
                const reference = warehouse.code && !primaryLabel.includes(warehouse.code)
                    ? ` - ${warehouse.code}`
                    : '';
                byValue.set(value, {
                    label: `${primaryLabel}${reference}`,
                    value,
                    locationId: warehouse.locationId,
                    supplierClientId: warehouse.supplierClientId,
                    supplierClientName: warehouse.supplierClientName,
                    warehouse
                });
            });

        return Array.from(byValue.values())
            .sort((first, second) => `${first.label}`.localeCompare(`${second.label}`));
    }

    private sourceWarehouseLabel(source: PosmProjectItemSource): string {
        return source.sourceWarehousingWarehouseName
            || this.warehouseOptions().find(option => option.value === source.sourceWarehousingWarehouseId)?.label
            || source.sourceWarehouseName
            || `warehouse #${source.sourceWarehousingWarehouseId || source.sourceWarehouseId || '-'}`;
    }

    private buildSupplierOptions(clients: CrmClient[]): any[] {
        return [...(clients || [])]
            .filter(client => !!client?.id)
            .map(client => ({
                label: client.clientNumber ? `${client.companyName} (${client.clientNumber})` : client.companyName,
                value: client.id,
                client
            }))
            .sort((left, right) => `${left.label || ''}`.localeCompare(`${right.label || ''}`));
    }

    private selectedWarehouse(source: PosmProjectItemSource): WarehousingWarehouseOption | undefined {
        return this.warehouseOptions().find(option => option.value === source.sourceWarehousingWarehouseId);
    }

    private preferredSupplierClientId(source: PosmProjectItemSource): number | undefined {
        return this.selectedWarehouse(source)?.supplierClientId || source.supplierClientId || undefined;
    }

    private normalizeSource(source: PosmProjectItemSource): PosmProjectItemSource {
        const normalized = {
            ...source,
            reservations: source.reservations || [],
            allocationTargets: source.allocationTargets || []
        };
        if (!normalized.sourceWarehousingWarehouseId && normalized.sourceWarehouseId) {
            const matchedWarehouse = this.warehouseOptions().find(
                option => option.locationId === normalized.sourceWarehouseId
            );
            if (matchedWarehouse) {
                normalized.sourceWarehousingWarehouseId = matchedWarehouse.value;
                normalized.sourceWarehousingWarehouseName ||= matchedWarehouse.label;
            }
        }
        if (!normalized.sourceWarehousingWarehouseName && normalized.sourceWarehousingWarehouseId) {
            normalized.sourceWarehousingWarehouseName = this.warehouseOptions().find(
                option => option.value === normalized.sourceWarehousingWarehouseId
            )?.label;
        }
        if (!normalized.requestedConditionName && normalized.requestedConditionId) {
            normalized.requestedConditionName = this.conditionOptions().find(
                condition => condition.id === normalized.requestedConditionId
            )?.name;
        }
        normalized.coverageMode = this.coverageMode(normalized);
        return normalized;
    }

    private sourceSaveErrorDetail(error: any): string {
        const rawMessage = `${error?.error?.message || error?.error || error?.message || ''}`.trim();
        const normalized = rawMessage.toLowerCase();
        if (normalized.includes('warehouse does not belong to selected company')
            || normalized.includes('warehouse location does not belong to selected company')
            || normalized.includes('selected source location is not a warehouse')
            || normalized.includes('warehousing warehouse does not belong to selected company')) {
            return 'Selected Warehousing warehouse does not belong to selected company.';
        }
        return rawMessage || 'Unable to save source line.';
    }
}
